import { ROUTE_SIGN_UP_FLOW } from "apps/website/constants/routes";

import { IHeaderLink } from "../../base/Button/Button.map";

export interface IHeaderAuth {
  firstName: string;
  lastName?: string;
}

export interface IHeaderHistoryNavigation {
  hide?: boolean;
  back?: () => void;
  forward?: () => void;
}

export interface IHeaderLinks {
  primaryLinks: IHeaderLink[];
  secondaryLinks: IHeaderLink[];
  authLinks: IHeaderLink[];
  guestLinks: IHeaderLink[];
}

const GUEST_LOGIN_LINK: IHeaderLink = {
  id: "g-0",
  name: "Login",
  nameKey: "GuestLinks.login",
  to: "/login",
};

const GUEST_TRIAL_LINK_BASE = {
  id: "g-1",
  name: "Start my trial",
  nameKey: "GuestLinks.startMyTrial",
  type: "cta",
} as const;

export const HEADER_LINKS_NONE: IHeaderLinks = {
  primaryLinks: [],
  secondaryLinks: [],
  authLinks: [],
  guestLinks: [],
};

export const HEADER_LINKS_DEFAULT: IHeaderLinks = {
  primaryLinks: [
    {
      id: "p-0",
      name: "Our products",
      nameKey: "PrimaryLinks.ourProducts",
      to: "/products",
      children: [
        {
          id: "p-0.1",
          name: "Fresh food and treats",
          nameKey: "PrimaryLinks.freshFoodAndTreats",
          to: "/products",
          image: {
            src: "/images/dev/components/Header/sub-nav-products-fresh.webp",
            width: 310,
            height: 310,
          },
        },
        {
          id: "p-0.2",
          name: "Scoop planet litter",
          nameKey: "PrimaryLinks.scoopPlanetLitter",
          to: "/planet-litter",
          image: {
            src: "/images/dev/components/Header/sub-nav-products-scoop.webp",
            width: 310,
            height: 310,
          },
        },
      ],
    },
    {
      id: "p-1",
      name: "Why fresh",
      nameKey: "PrimaryLinks.whyFresh",
      to: "/why-fresh",
    },
    { id: "p-2", name: "About us", nameKey: "PrimaryLinks.aboutUs", to: "/about-us" },
    { id: "p-3", name: "Learn", nameKey: "PrimaryLinks.learn", to: "/articles" },
  ],
  secondaryLinks: [],
  authLinks: [
    { id: "a-0", name: "My Homepage", nameKey: "AuthLinks.myHomepage", to: "/customer" },
    { id: "a-1", name: "My plans", nameKey: "AuthLinks.myPlans", to: "/customer/my-plans" },
    { id: "a-2", name: "My details", nameKey: "AuthLinks.myDetails", to: "/customer/my-details" },
    { id: "a-3", name: "My cats", nameKey: "AuthLinks.myCats", to: "/customer/cat-details" },
    { id: "a-4", name: "Free food", nameKey: "AuthLinks.freeFood", to: "/refer-a-friend?situation=hamburger_link" },
    { id: "a-5", name: "Orders", nameKey: "AuthLinks.orders", to: "/customer/past-orders" },
    { id: "a-6", name: "Help", nameKey: "AuthLinks.help", to: "https://help.katkin.com/en" },
    { id: "a-7", name: "Logout", nameKey: "AuthLinks.logout", to: "#", type: "logout" },
  ],
  guestLinks: [
    GUEST_LOGIN_LINK,
    { ...GUEST_TRIAL_LINK_BASE, to: ROUTE_SIGN_UP_FLOW, withAssociatedDiscount: true },
  ],
};

export const HEADER_LINKS_META: IHeaderLinks = HEADER_LINKS_DEFAULT;

export const HEADER_LINKS_FRESH_FOOD_NEW_USER: IHeaderLinks = HEADER_LINKS_DEFAULT;

export const HEADER_LINKS_FRESH_FEAST_TRIAL: IHeaderLinks = {
  ...HEADER_LINKS_DEFAULT,
  guestLinks: [
    GUEST_LOGIN_LINK,
    { ...GUEST_TRIAL_LINK_BASE, to: ROUTE_SIGN_UP_FLOW },
  ],
};

export const HEADER_LINKS_FRESH_FEAST_TRIAL_AC: IHeaderLinks = {
  ...HEADER_LINKS_DEFAULT,
  guestLinks: [
    GUEST_LOGIN_LINK,
    { ...GUEST_TRIAL_LINK_BASE, to: `${ROUTE_SIGN_UP_FLOW}?discount_code=MEOWAC30` },
  ],
};

export const HEADER_LINKS_SIGNUP_FLOW_V2_AC: IHeaderLinks = {
  ...HEADER_LINKS_DEFAULT,
  guestLinks: [
    GUEST_LOGIN_LINK,
    { ...GUEST_TRIAL_LINK_BASE, to: `${ROUTE_SIGN_UP_FLOW}?discount_code=MEOWAC30` },
  ],
};

export const HEADER_LINKS_SUFV2: IHeaderLinks = {
  ...HEADER_LINKS_DEFAULT,
  guestLinks: [
    GUEST_LOGIN_LINK,
    { ...GUEST_TRIAL_LINK_BASE, to: ROUTE_SIGN_UP_FLOW, withAssociatedDiscount: true },
  ],
};

export const HEADER_LINKS_LOGGED_IN: IHeaderLinks = {
  primaryLinks: [
    { id: "p-0", name: "Home", nameKey: "LoggedInLinks.home", to: "/customer" },
    { id: "p-1", name: "My Plans", nameKey: "LoggedInLinks.myPlans", to: "/customer/my-plans" },
    { id: "p-4", name: "My cats", nameKey: "LoggedInLinks.myCats", to: "/customer/cat-details" },
    { id: "p-2", name: "My details", nameKey: "LoggedInLinks.myDetails", to: "/customer/my-details" },
    { id: "p-4", name: "Past orders", nameKey: "LoggedInLinks.pastOrders", to: "/customer/past-orders" },
    { id: "p-6", name: "Learn", nameKey: "LoggedInLinks.learn", to: "/articles" },
  ],
  secondaryLinks: [
    { id: "s-1", name: "Gift a box", nameKey: "LoggedInLinks.giftABox", to: "/refer-a-friend" },
    { id: "s-2", name: "Help", nameKey: "LoggedInLinks.help", to: "/help" },
    { id: "s-3", name: "Logout", nameKey: "LoggedInLinks.logout", to: "#", type: "logout" },
  ],
  authLinks: [],
  guestLinks: [],
};

export const HEADER_LINKS_BREEDER: IHeaderLinks = {
  ...HEADER_LINKS_DEFAULT,
  authLinks: [
    { id: "a-0", name: "Send a box", nameKey: "BreederLinks.sendABox", to: "/forms/gccf-new-kitten" },
    { id: "a-1", name: "Help", nameKey: "BreederLinks.help", to: "https://faq.katkin.com/" },
    { id: "a-2", name: "Logout", nameKey: "BreederLinks.logout", to: "#", type: "logout" },
    { id: "a-3", name: "Send a box", nameKey: "BreederLinks.sendABox", to: "/forms/gccf-new-kitten", type: "cta" },
  ],
  guestLinks: [
    GUEST_LOGIN_LINK,
    { id: "g-1", name: "Register", nameKey: "BreederLinks.register", to: "/forms/new-breeder", type: "cta" },
  ],
};

export const HEADER_LINKS_SCOOP_PLANET: IHeaderLinks = {
  ...HEADER_LINKS_DEFAULT,
  guestLinks: [
    GUEST_LOGIN_LINK,
    { ...GUEST_TRIAL_LINK_BASE, to: "/forms/scoop-planet-new-user", withAssociatedDiscount: true },
  ],
};
